import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { useOverlayContainer } from "@design-system/overlay/OverlayContainerContext";
import { LinkIcon } from "@icons";
import { Editor, Range } from "slate";
import { useFocused, useSlate } from "slate-react";
import {
    hideHoveringToolbar,
    resolveHoveringToolbarContainer,
    showHoveringToolbar,
} from "./HoveringToolbar.position";
import { isLinkActive, unwrapLink } from "./SlateEditor.lib";
import { isListActive, toggleList } from "./SlateEditor.lists";
import { isMarkActive, toggleMark } from "./SlateEditor.marks";
import styles from "./SlateEditor.module.scss";

interface HoveringToolbarProps {
    disabled?: boolean;
    onAddLink: () => void;
}

export const HoveringToolbar = ({
    disabled,
    onAddLink,
}: HoveringToolbarProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const editor = useSlate();
    const inFocus = useFocused();
    const { container: overlayContainer, containerRef } = useOverlayContainer();
    const portalContainer = resolveHoveringToolbarContainer({
        overlayContainer,
        overlayContainerEl: containerRef?.current,
    });

    useEffect(() => {
        const element = ref.current;
        const { selection } = editor;

        if (!element) {
            return;
        }

        if (
            !selection ||
            !inFocus ||
            Range.isCollapsed(selection) ||
            Editor.string(editor, selection) === ""
        ) {
            hideHoveringToolbar(element);
            return;
        }

        const domSelection = window.getSelection();
        if (!domSelection || domSelection.rangeCount === 0) {
            return;
        }

        const domRange = domSelection.getRangeAt(0);
        showHoveringToolbar({
            element,
            container: portalContainer,
            selectionRect: domRange.getBoundingClientRect(),
        });
    });

    return ReactDOM.createPortal(
        <div
            ref={ref}
            className={styles.hoveringToolbar}
            data-base-ui-swipe-ignore
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.preventDefault()}
        >
            <Button
                type="button"
                variant="secondary"
                className={classNames({
                    [styles.hoveringToolbarButtonActive]: isMarkActive(
                        editor,
                        "bold",
                    ),
                })}
                onMouseDown={(e) => {
                    e.preventDefault();
                    toggleMark(editor, "bold");
                }}
                disabled={disabled}
                size="sm"
                iconOnly
                aria-label="Bold"
            >
                <strong>B</strong>
            </Button>
            <Button
                type="button"
                variant="secondary"
                className={classNames({
                    [styles.hoveringToolbarButtonActive]: isMarkActive(
                        editor,
                        "italic",
                    ),
                })}
                onMouseDown={(e) => {
                    e.preventDefault();
                    toggleMark(editor, "italic");
                }}
                disabled={disabled}
                size="sm"
                iconOnly
                aria-label="Italic"
            >
                <em>I</em>
            </Button>
            <Button
                type="button"
                variant="secondary"
                className={classNames({
                    [styles.hoveringToolbarButtonActive]: isMarkActive(
                        editor,
                        "underline",
                    ),
                })}
                onMouseDown={(e) => {
                    e.preventDefault();
                    toggleMark(editor, "underline");
                }}
                disabled={disabled}
                size="sm"
                iconOnly
                aria-label="Underline"
            >
                <u>U</u>
            </Button>
            <span className={styles.hoveringToolbarDivider} />
            <Button
                type="button"
                variant="secondary"
                className={classNames({
                    [styles.hoveringToolbarButtonActive]: isLinkActive(editor),
                })}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                    if (isLinkActive(editor)) {
                        unwrapLink(editor);
                        return;
                    }
                    onAddLink();
                }}
                disabled={disabled}
                title={isLinkActive(editor) ? "Remove link" : "Add link"}
                size="sm"
                iconOnly
                aria-label={isLinkActive(editor) ? "Remove link" : "Add link"}
            >
                <LinkIcon />
            </Button>
            <Button
                type="button"
                variant="secondary"
                className={classNames({
                    [styles.hoveringToolbarButtonActive]: isListActive(
                        editor,
                        "bulleted-list",
                    ),
                })}
                onMouseDown={(e) => {
                    e.preventDefault();
                    toggleList(editor, "bulleted-list");
                }}
                disabled={disabled}
                title="Bullet list"
                size="sm"
                iconOnly
                aria-label="Bullet list"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    aria-hidden="true"
                >
                    <circle cx="2.5" cy="3" r="1.1" fill="currentColor" />
                    <rect
                        x="5.5"
                        y="2.2"
                        width="7"
                        height="1.6"
                        rx="0.4"
                        fill="currentColor"
                    />
                    <circle cx="2.5" cy="7" r="1.1" fill="currentColor" />
                    <rect
                        x="5.5"
                        y="6.2"
                        width="7"
                        height="1.6"
                        rx="0.4"
                        fill="currentColor"
                    />
                    <circle cx="2.5" cy="11" r="1.1" fill="currentColor" />
                    <rect
                        x="5.5"
                        y="10.2"
                        width="7"
                        height="1.6"
                        rx="0.4"
                        fill="currentColor"
                    />
                </svg>
            </Button>
            <Button
                type="button"
                variant="secondary"
                className={classNames({
                    [styles.hoveringToolbarButtonActive]: isListActive(
                        editor,
                        "numbered-list",
                    ),
                })}
                onMouseDown={(e) => {
                    e.preventDefault();
                    toggleList(editor, "numbered-list");
                }}
                disabled={disabled}
                title="Numbered list"
                size="sm"
                iconOnly
                aria-label="Numbered list"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    aria-hidden="true"
                >
                    <text
                        x="0"
                        y="4.5"
                        fontSize="5"
                        fontWeight="700"
                        fill="currentColor"
                    >
                        1
                    </text>
                    <rect
                        x="5.5"
                        y="2.2"
                        width="7"
                        height="1.6"
                        rx="0.4"
                        fill="currentColor"
                    />
                    <text
                        x="0"
                        y="8.5"
                        fontSize="5"
                        fontWeight="700"
                        fill="currentColor"
                    >
                        2
                    </text>
                    <rect
                        x="5.5"
                        y="6.2"
                        width="7"
                        height="1.6"
                        rx="0.4"
                        fill="currentColor"
                    />
                    <text
                        x="0"
                        y="12.5"
                        fontSize="5"
                        fontWeight="700"
                        fill="currentColor"
                    >
                        3
                    </text>
                    <rect
                        x="5.5"
                        y="10.2"
                        width="7"
                        height="1.6"
                        rx="0.4"
                        fill="currentColor"
                    />
                </svg>
            </Button>
        </div>,
        portalContainer,
    );
};
